// EL SONIDO DE LA CAMPANA Y EL DE LOS ANUNCIOS.
//
// Lissette, 2026-09-02, queja 10: "la opcion de colocarle sonido a la campana
// de notificacion y de anuncio, algo que suene duro y diferente ambos".
//
// ── POR QUE SE SINTETIZA Y NO SE CARGA UN .MP3 ─────────────────────────────
//
// Un fichero de audio habria que servirlo desde algun sitio, y la CSP del CRM
// no deja cargar medios de fuera. Meterlo en el bundle como data: URI son
// decenas de kilobytes que se bajan SIEMPRE, incluso con el sonido apagado,
// para dos pitidos de medio segundo. La Web Audio API los genera en el momento
// con unas pocas lineas, sin fichero, sin red y sin tocar la CSP.
//
// ── LOS NAVEGADORES NO DEJAN SONAR SIN PERMISO ─────────────────────────────
//
// Chrome y Safari crean el AudioContext "suspendido" hasta que la persona
// interactua con la pagina; si no, cualquier web podria hacer ruido sola. Por
// eso el contexto se crea PEREZOSO (en el primer intento de sonar, no al
// cargar) y se intenta reanudar en el primer clic o tecla. Consecuencia
// honesta: el primer aviso que llegue antes de que nadie toque nada puede no
// oirse. Es una regla del navegador, no algo que se pueda "arreglar" aqui.
//
// ── NADA DE ESTO PUEDE ROMPER LA PANTALLA ──────────────────────────────────
//
// Todo va envuelto: un navegador sin Web Audio, un contexto que no arranca o
// una pestana en segundo plano dejan la aplicacion exactamente igual. Un aviso
// que no suena es un fastidio; una pantalla en blanco por un pitido, no.
import { prefsGet, prefsSet } from './uiPrefsStore.js';

const CLAVE = 'deskte:sonidoDeAvisos';

/** ¿Suenan los avisos? Encendido por defecto: ella lo pidio para oirlos. */
export function sonidoActivado() {
  try {
    const v = prefsGet(CLAVE);
    return v === null || v === undefined ? true : v !== 'off' && v !== false;
  } catch { return true; }
}

export function ponerSonido(encendido) {
  try { prefsSet(CLAVE, encendido ? 'on' : 'off'); } catch { /* sin prefs, se queda como este */ }
}

let ctx = null;
function contexto() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    try { ctx = new AC(); } catch { return null; }
  }
  if (ctx.state === 'suspended') { try { ctx.resume(); } catch { /* lo reintenta el proximo */ } }
  return ctx;
}

// El primer gesto de la persona desbloquea el audio para el resto de la sesion.
if (typeof window !== 'undefined') {
  const despertar = () => { contexto(); };
  window.addEventListener('pointerdown', despertar, { once: true, passive: true });
  window.addEventListener('keydown', despertar, { once: true, passive: true });
}

/**
 * Una nota. `volumen` alto a proposito: ella pidio "algo que suene duro".
 * La envolvente (subida de 12 ms, bajada suave) evita el chasquido que da
 * arrancar y cortar una onda en seco.
 */
function nota(ac, { frecuencia, empieza, dura, tipo = 'sine', volumen = 0.5 }) {
  const osc = ac.createOscillator();
  const gan = ac.createGain();
  osc.type = tipo;
  osc.frequency.setValueAtTime(frecuencia, ac.currentTime + empieza);
  gan.gain.setValueAtTime(0.0001, ac.currentTime + empieza);
  gan.gain.exponentialRampToValueAtTime(volumen, ac.currentTime + empieza + 0.012);
  gan.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + empieza + dura);
  osc.connect(gan);
  gan.connect(ac.destination);
  osc.start(ac.currentTime + empieza);
  osc.stop(ac.currentTime + empieza + dura + 0.02);
}

function tocar(notas) {
  if (!sonidoActivado()) return;
  const ac = contexto();
  if (!ac) return;
  try { notas.forEach((n) => nota(ac, n)); } catch { /* un aviso mudo no rompe nada */ }
}

/**
 * CAMPANA DE NOTIFICACION: dos notas que SUBEN, timbre limpio.
 * Es el aviso de "tienes algo que atender".
 */
export function sonarNotificacion() {
  tocar([
    { frecuencia: 880, empieza: 0, dura: 0.16, tipo: 'sine', volumen: 0.55 },
    { frecuencia: 1318.5, empieza: 0.13, dura: 0.28, tipo: 'sine', volumen: 0.55 },
  ]);
}

/**
 * CAMPANITA DE CITA NUEVA: "tilin-tilin", dos golpes de campana.
 *
 * Wendy Cercio, 2026-10-27: "que suene la campanita cada vez que alguien
 * agende una cita por booking link". Una cita reservada es lo unico que llega
 * con un cliente esperando al otro lado, asi que lleva su propio sonido y no
 * el pitido de cualquier aviso.
 *
 * Una campana real no es una onda limpia: el golpe suena a la vez en la nota y
 * en sus parciales altos, y se apaga despacio. Por eso cada golpe son tres
 * ondas a la vez (la nota, ~2,76x y ~5,4x, los parciales de una campana) con
 * una cola larga — se distingue a la primera de los otros dos sonidos.
 */
export function sonarCita() {
  const golpe = (empieza) => [
    { frecuencia: 1318.5, empieza, dura: 1.1, tipo: 'sine', volumen: 0.5 },
    { frecuencia: 3639, empieza, dura: 0.6, tipo: 'sine', volumen: 0.18 },
    { frecuencia: 7120, empieza, dura: 0.3, tipo: 'sine', volumen: 0.07 },
  ];
  tocar([...golpe(0), ...golpe(0.32)]);
}

/** Los avisos que suenan con la campanita en vez del pitido normal. */
export function esCitaReservada(tipo) {
  return tipo === 'appointment_booked';
}

/**
 * ANUNCIO: tres notas que BAJAN, timbre mas grueso y mas grave.
 *
 * Distinto del de arriba en las tres cosas que el oido usa para separar dos
 * sonidos —direccion, numero de notas y timbre—, no solo en el tono: dos
 * pitidos que solo se diferencian en la altura se confunden a diario. Ella
 * pidio expresamente que fueran "diferente ambos".
 */
export function sonarAnuncio() {
  tocar([
    { frecuencia: 784, empieza: 0, dura: 0.18, tipo: 'triangle', volumen: 0.6 },
    { frecuencia: 659.3, empieza: 0.15, dura: 0.18, tipo: 'triangle', volumen: 0.6 },
    { frecuencia: 523.3, empieza: 0.3, dura: 0.34, tipo: 'triangle', volumen: 0.6 },
  ]);
}
